import React from 'react'
import {
  Box,
  Typography,
  styled,
  Tooltip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { CustomerService } from '@/api/services/CustomerService'
import { ErrorPage } from '@/pages/Error'
import SearchLogo from '@/assets/search.svg'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import MoreVertIcon from '@mui/icons-material/MoreVert'

export const Eligibility: React.FC = () => {
  const {
    data: consentDetails,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['eligibility'],
    queryFn: () => CustomerService.fetchEligibiltyConsentDetails(),
  })

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleRevokeConsent = async () => {
    handleMenuClose()
    try {
      await CustomerService.revokeConsentAction()
    } catch (error) {
      console.error('Failed to revoke consent:', error)
    }
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={5}>
        <CircularProgress size={25} sx={{ color: 'lightgray' }} />
      </Box>
    )
  }

    if (isError) {
      return <ErrorPage title="Unable to Fetch the Consents" message="" />
    }

    if (!consentDetails || consentDetails.length === 0) {
      return (
        <EmptyContainer>
          <Box component="img" src={SearchLogo} alt="Search Icon" my={3} />
          <Title>No Consents Found</Title>
        </EmptyContainer>
      )
    }

  return (
    <Box>
      <Typography variant="h6" pb={3}>
        Eligibility
      </Typography>
      <Row>
        <Box sx={{ display: 'flex', gap: '5rem' }}>
          <Typography className="label">Electronic Comms Consent</Typography>

          <Box display="flex" alignItems="center" gap={1}>
            <Typography className="status">Consent given</Typography>
            <Tooltip title="Consent is active">
              <CheckCircleIcon color="success" fontSize="small" />
            </Tooltip>
          </Box>
        </Box>

        <IconButton size="small" onClick={handleMenuOpen}>
          <MoreVertIcon />
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={handleRevokeConsent}>Revoke consent</MenuItem>
        </Menu>
      </Row>
    </Box>
  )
}

const Row = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: '1px solid #E4E7EC',
  paddingBottom: '12px',
}))

const EmptyContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
})

const Title = styled(Typography)(({ theme }) => ({
  ...theme.typography.h6,
  fontSize: '0.9rem',
  paddingBottom: theme.spacing(3),
}))